import React, {useEffect} from "react";
import BaseList from "../../../../components/partials/common/base-list";
import {useTranslation} from "react-i18next";
import {getPathParam} from "../../../../utils/converter";
import {COMPANY, COMPANY_VAT, COMPANY_VATS} from "../../../../api/endpoints";
import {useSelector} from "react-redux";
import ActionsMenu from "../../../../components/common/table/actions-menu";
import CardDetail from "../../../../components/common/list/card-detail";
import TableDetail from "../../../../components/common/list/table-detail";
import {Controller, useForm} from "react-hook-form";
import InputText from "../../../../components/common/form/input-text";
import InputFormattedNumber from "../../../../components/common/form/input-formatted-number";
import {Warning} from "../../../../components/common/alert/banner";
import InputSubmit from "../../../../components/common/form/input-submit";
import {ReceiptTaxIcon} from "@heroicons/react/outline";
import BasePermissionWrapper
  from "../../../../components/partials/restricted/base-permission-wrapper";
import {
  getPermission,
  permissionGroup,
  permissionSpecific
} from "../../../../constants/permissions";

const toVatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatVatValue = (value) => {
  const numericValue = toVatNumber(value);

  if (numericValue === null) {
    return "-";
  }

  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });

  return `${formatter.format(numericValue)}%`;
};

const VatConfiguration = () => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  return (
    <BasePermissionWrapper
      requiredPermissions={[
        getPermission(
          permissionGroup.COMPANY_CONFIGURATION,
          permissionSpecific.COMPANY_CONF_VAT
        )
      ]}
      renderBaseOnFail={true}
    >
      {loggedCompany && (
        <BaseList
          columns={[
            t("app.vat.name", { defaultValue: "Name" }),
            t("app.vat.code", { defaultValue: "Code" }),
            t("app.vat.rate", { defaultValue: "VAT %" }),
            ""
          ]}
          endpoint={getPathParam([COMPANY, loggedCompany.id, COMPANY_VATS])}
          storeEndpoint={getPathParam([COMPANY, loggedCompany.id, COMPANY_VAT])}
          updateEndpoint={getPathParam([
            COMPANY,
            loggedCompany.id,
            COMPANY_VAT,
            ":id"
          ])}
          baseResult={{ total_rows: 0, company_vats: [] }}
          resultLabel={"company_vats"}
          title={t("app.vat.title", { defaultValue: "VAT Management" })}
          showHeader={true}
          mobRow={MobRow}
          deskRow={DeskRow}
          addForm={AddVat}
          editForm={EditVat}
          icon={ReceiptTaxIcon}
          addFormLabel={t("app.vat.add", { defaultValue: "Add VAT rate" })}
          btnAddLabel={t("app.vat.add", { defaultValue: "Add VAT rate" })}
          editTitle={t("app.vat.edit_title", { defaultValue: "Edit VAT rate" })}
          isActiveEdit={true}
        />
      )}
    </BasePermissionWrapper>
  );
};

const MobRow = ({ element, extras }) => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  const removeDetail = {
    title: t("app.vat.remove_title", { defaultValue: "Remove VAT rate" }),
    message: t("app.vat.remove_message", { defaultValue: "Are you sure you want to delete this VAT rate?" }),
    endpoint: getPathParam([COMPANY, loggedCompany.id, COMPANY_VAT, element.id])
  };

  return (
    <li key={element.id} className="flex items-center gap-4 p-4">
      <div className="flex-grow">
        <div className="pb-4 items-center flex justify-between">
          <div>
            <div className="text-sm font-semibold text-gray-900">
              {element.name}
            </div>
            <div className="text-xs text-gray-500 uppercase">
              {element.code}
            </div>
          </div>
          <ActionsMenu>
            <button
              className={"text-left"}
              onClick={() => {
                extras.onEdit(true, element);
              }}
            >
              {t("app.common.edit", { defaultValue: "Edit" })}
            </button>
            <button
              className={"text-left"}
              onClick={() => {
                extras.onRemove(true, removeDetail);
              }}
            >
              {t("app.common.remove", { defaultValue: "Remove" })}
            </button>
          </ActionsMenu>
        </div>
        <div className="flex flex-col">
          <CardDetail title={t("app.vat.rate", { defaultValue: "VAT %" })}>
            {formatVatValue(element.value)}
          </CardDetail>
          <CardDetail title={t("app.common.description", { defaultValue: "Description" })}>
            {element.description ?? "-"}
          </CardDetail>
        </div>
      </div>
    </li>
  );
};

const DeskRow = ({ element, extras }) => {
  const { t } = useTranslation();
  const { loggedCompany } = useSelector((state) => state.user);

  const removeDetail = {
    title: t("app.vat.remove_title", { defaultValue: "Remove VAT rate" }),
    message: t("app.vat.remove_message", { defaultValue: "Are you sure you want to delete this VAT rate?" }),
    endpoint: getPathParam([COMPANY, loggedCompany.id, COMPANY_VAT, element.id])
  };

  return (
    <tr key={element.id}>
      <TableDetail extraClass="max-w-sm">
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-gray-900">
            {element.name}
          </span>
          <span className="text-xs text-gray-500 uppercase">
            {element.code}
          </span>
        </div>
      </TableDetail>

      <TableDetail>
        <span className="text-sm text-gray-900">
          {element.description ?? "-"}
        </span>
      </TableDetail>

      <TableDetail extraClass="w-32">
        <span className="text-sm font-semibold text-gray-900">
          {formatVatValue(element.value)}
        </span>
      </TableDetail>

      <TableDetail>
        <ActionsMenu>
          <button
            className={"text-left"}
            onClick={() => {
              extras.onEdit(true, element);
            }}
          >
            {t("app.common.edit", { defaultValue: "Edit" })}
          </button>
          <button
            className={"text-left"}
            onClick={() => {
              extras.onRemove(true, removeDetail);
            }}
          >
            {t("app.common.remove", { defaultValue: "Remove" })}
          </button>
        </ActionsMenu>
      </TableDetail>
    </tr>
  );
};

const AddVat = ({ exception, isLoading, onSubmitData }) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: "",
      code: "",
      description: "",
      value: null
    }
  });

  const onSubmit = (data) => {
    const payload = {
      ...data,
      name: data.name?.trim(),
      code: data.code?.trim(),
      description: data.description?.trim() || null,
      value: toVatNumber(data.value)
    };
    onSubmitData(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <InputText
          id={"vat_name"}
          type={"text"}
          label={t("app.vat.name", { defaultValue: "Name" })}
          errors={errors.name}
          isRequired={true}
          input={{
            ...register("name", {
              required: true,
              validate: (value) => (value ?? "").trim().length > 0
            })
          }}
        />

        <InputText
          id={"vat_code"}
          type={"text"}
          label={t("app.vat.code", { defaultValue: "Code" })}
          errors={errors.code}
          isRequired={true}
          input={{
            ...register("code", {
              required: true,
              validate: (value) => (value ?? "").trim().length > 0
            })
          }}
        />

        <Controller
          control={control}
          name="value"
          rules={{ required: true, min: 0 }}
          render={({ field: { onChange, value } }) => (
            <InputFormattedNumber
              id={"vat_value"}
              label={t("app.vat.rate", { defaultValue: "VAT %" })}
              errors={errors.value}
              isFullW={true}
              isRequired={true}
              suffix="%"
              decimalScale={2}
              allowNegative={false}
              value={toVatNumber(value)}
              onChange={onChange}
            />
          )}
        />

        <InputText
          id={"vat_description"}
          type={"textarea-2"}
          label={t("app.common.description", { defaultValue: "Description" })}
          errors={errors.description}
          input={{ ...register("description") }}
        />
      </div>

      {exception && <Warning message={exception} />}

      <div className="mt-3">
        <InputSubmit
          isLoading={isLoading}
          isFullWith={true}
          label={t("app.form.save", { defaultValue: "Save" })}
        />
      </div>
    </form>
  );
};

const EditVat = ({ data, exception, isEditLoading, onSubmitData }) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      id: data?.id ?? "",
      name: data?.name ?? "",
      code: data?.code ?? "",
      description: data?.description ?? "",
      value: toVatNumber(data?.value)
    }
  });

  useEffect(() => {
    reset({
      id: data?.id ?? "",
      name: data?.name ?? "",
      code: data?.code ?? "",
      description: data?.description ?? "",
      value: toVatNumber(data?.value)
    });
  }, [data, reset]);

  const onSubmit = (formData) => {
    const payload = {
      ...formData,
      id: data?.id ?? "",
      name: formData.name?.trim(),
      code: formData.code?.trim(),
      description: formData.description?.trim() || null,
      value: toVatNumber(formData.value)
    };
    onSubmitData(payload);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <InputText
          id={"vat_name_edit"}
          type={"text"}
          label={t("app.vat.name", { defaultValue: "Name" })}
          errors={errors.name}
          isRequired={true}
          input={{
            ...register("name", {
              required: true,
              validate: (value) => (value ?? "").trim().length > 0
            })
          }}
        />

        <InputText
          id={"vat_code_edit"}
          type={"text"}
          label={t("app.vat.code", { defaultValue: "Code" })}
          errors={errors.code}
          isRequired={true}
          input={{
            ...register("code", {
              required: true,
              validate: (value) => (value ?? "").trim().length > 0
            })
          }}
        />

        <Controller
          control={control}
          name="value"
          rules={{ required: true, min: 0 }}
          render={({ field: { onChange, value } }) => (
            <InputFormattedNumber
              id={"vat_value_edit"}
              label={t("app.vat.rate", { defaultValue: "VAT %" })}
              errors={errors.value}
              isFullW={true}
              isRequired={true}
              suffix="%"
              decimalScale={2}
              allowNegative={false}
              value={toVatNumber(value)}
              onChange={onChange}
            />
          )}
        />

        <InputText
          id={"vat_description_edit"}
          type={"textarea-2"}
          label={t("app.common.description", { defaultValue: "Description" })}
          errors={errors.description}
          input={{ ...register("description") }}
        />
      </div>

      {exception && <Warning message={exception} />}

      <div className="mt-3">
        <InputSubmit
          isLoading={isEditLoading}
          isFullWith={true}
          label={t("app.form.save", { defaultValue: "Save" })}
        />
      </div>
    </form>
  );
};

export default VatConfiguration;
